import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  getDeliveryPartners,
  getPartnerDetails,
  updatePartnerStatus as updatePartnerStatusService,
  createPartner as createPartnerService,
  updatePartner as updatePartnerService,
  verifyDocument as verifyDocumentService,
  getActiveDeliveries,
  getPartnerEarnings,
  settleCOD as settleCODService,
  getPartnerRatings,
  getPartnerOrders,
} from "services/deliveryPartnerService"
import { getApiErrorMessage } from "utils/apiErrorMessage"

const getDeliveryPartnerErrorMessage = (err, fallback) =>
  getApiErrorMessage(null, err, { fallback })

export const fetchPartners = createAsyncThunk(
  "deliveryPartner/fetchPartners",
  async (filters, { rejectWithValue }) => {
    try {
      const data = await getDeliveryPartners(filters)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch riders")
      return data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch riders"))
    }
  }
)

export const fetchPartnerDetails = createAsyncThunk(
  "deliveryPartner/fetchPartnerDetails",
  async (id, { rejectWithValue }) => {
    try {
      const data = await getPartnerDetails(id)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch rider details")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch rider details"))
    }
  }
)

export const updatePartnerStatus = createAsyncThunk(
  "deliveryPartner/updatePartnerStatus",
  async ({ id, status, reason }, { rejectWithValue }) => {
    try {
      const data = await updatePartnerStatusService(id, status, reason)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to update status")
      return { id, status, reason, partner: data.data }
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to update status"))
    }
  }
)

export const createPartner = createAsyncThunk(
  "deliveryPartner/createPartner",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await createPartnerService(payload)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to create rider")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to create rider"))
    }
  }
)

export const updatePartnerProfile = createAsyncThunk(
  "deliveryPartner/updatePartnerProfile",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const data = await updatePartnerService(id, payload)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to update rider")
      return { id, payload }
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to update rider"))
    }
  }
)

export const verifyDocument = createAsyncThunk(
  "deliveryPartner/verifyDocument",
  async ({ partnerId, documentId, status, reason }, { rejectWithValue }) => {
    try {
      const data = await verifyDocumentService(partnerId, documentId, status, reason)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to verify document")
      return { partnerId, documentId, status, reason }
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to verify document"))
    }
  }
)

export const fetchActiveOrders = createAsyncThunk(
  "deliveryPartner/fetchActiveOrders",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getActiveDeliveries()
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch active orders")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch active orders"))
    }
  }
)

export const fetchPartnerEarnings = createAsyncThunk(
  "deliveryPartner/fetchPartnerEarnings",
  async ({ id, period }, { rejectWithValue }) => {
    try {
      const data = await getPartnerEarnings(id, period)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch earnings")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch earnings"))
    }
  }
)

export const settleCOD = createAsyncThunk(
  "deliveryPartner/settleCOD",
  async ({ id, amount, date }, { rejectWithValue }) => {
    try {
      const data = await settleCODService(id, amount, date)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to settle COD")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to settle COD"))
    }
  }
)

export const getPartnerRatingsThunk = createAsyncThunk(
  "deliveryPartner/getPartnerRatings",
  async ({ id, page }, { rejectWithValue }) => {
    try {
      const data = await getPartnerRatings(id, page)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch ratings")
      return data.data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch ratings"))
    }
  }
)

export const fetchPartnerOrders = createAsyncThunk(
  "deliveryPartner/fetchPartnerOrders",
  async ({ id, page = 1, limit = 20, status = "all", filters = {} }, { rejectWithValue }) => {
    try {
      const data = await getPartnerOrders(id, { page, limit, status, ...filters })
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch orders")
      return data
    } catch (err) {
      return rejectWithValue(getDeliveryPartnerErrorMessage(err, "Failed to fetch orders"))
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  partners: [],
  selectedPartner: null,
  activeOrders: [],
  filtersCriteria: {},
  earnings: null,
  ratings: [],
  partnerOrders: [],
  partnerOrdersPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 20,
  },
  loading: {
    list: false,
    details: false,
    status: false,
    document: false,
    earnings: false,
    cod: false,
    orders: false,
  },
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 10,
  },
}

const deliveryPartnerSlice = createSlice({
  name: "deliveryPartner",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filtersCriteria = { ...state.filtersCriteria, ...action.payload }
    },
    resetFilters(state) {
      state.filtersCriteria = {}
    },
    clearError(state) {
      state.error = null
    },
    clearSelectedPartner(state) {
      state.selectedPartner = null
      state.partnerOrders = []
      state.partnerOrdersPagination = initialState.partnerOrdersPagination
    },
    setSelectedPartner(state, action) {
      state.selectedPartner = action.payload
    },
  },
  extraReducers: builder => {
    // fetchPartners
    builder
      .addCase(fetchPartners.pending, state => {
        state.loading.list = true
        state.error = null
      })
      .addCase(fetchPartners.fulfilled, (state, action) => {
        state.loading.list = false
        const payload = action.payload || {}
        state.partners = Array.isArray(payload)
          ? payload
          : payload.data ?? payload.partners ?? payload.items ?? []
        state.pagination.currentPage = payload.currentPage ?? payload.pageNo ?? 1
        state.pagination.totalPages = payload.totalPages ?? 1
        state.pagination.totalCount = payload.totalCount ?? state.partners.length ?? 0
        state.pagination.itemsPerPage = payload.itemsPerPage ?? payload.perPage ?? 10
      })
      .addCase(fetchPartners.rejected, (state, action) => {
        state.loading.list = false
        state.error = action.payload
      })

    // fetchPartnerDetails
    builder
      .addCase(fetchPartnerDetails.pending, state => {
        state.loading.details = true
        state.error = null
      })
      .addCase(fetchPartnerDetails.fulfilled, (state, action) => {
        state.loading.details = false
        state.selectedPartner = action.payload
      })
      .addCase(fetchPartnerDetails.rejected, (state, action) => {
        state.loading.details = false
        state.error = action.payload
      })

    // updatePartnerStatus
    builder
      .addCase(updatePartnerStatus.pending, state => {
        state.loading.status = true
        state.error = null
      })
      .addCase(updatePartnerStatus.fulfilled, (state, action) => {
        state.loading.status = false
        const { id, status, reason, partner } = action.payload
        const listPartner = state.partners.find(p => p._id === id || p.id === id)
        if (listPartner) {
          listPartner.status = status
          if (partner?.statusReason !== undefined) {
            listPartner.statusReason = partner.statusReason
          }
        }
        if (state.selectedPartner && (state.selectedPartner._id === id || state.selectedPartner.id === id)) {
          if (partner) {
            state.selectedPartner = {
              ...state.selectedPartner,
              ...partner,
              documents: state.selectedPartner.documents,
              bankDetails: state.selectedPartner.bankDetails,
            }
          } else {
            state.selectedPartner.status = status
            if (reason) state.selectedPartner.statusReason = reason
          }
        }
      })
      .addCase(updatePartnerStatus.rejected, (state, action) => {
        state.loading.status = false
        state.error = action.payload
      })

    // createPartner
    builder
      .addCase(createPartner.pending, state => {
        state.loading.status = true
        state.error = null
      })
      .addCase(createPartner.fulfilled, (state, action) => {
        state.loading.status = false
        if (action.payload) {
          state.partners.unshift(action.payload)
        }
      })
      .addCase(createPartner.rejected, (state, action) => {
        state.loading.status = false
        state.error = action.payload
      })

    // updatePartnerProfile
    builder
      .addCase(updatePartnerProfile.pending, state => {
        state.loading.status = true
        state.error = null
      })
      .addCase(updatePartnerProfile.fulfilled, (state, action) => {
        state.loading.status = false
        const { id, payload } = action.payload
        const partner = state.partners.find(p => p._id === id || p.id === id)
        if (partner) Object.assign(partner, payload)
        if (state.selectedPartner && (state.selectedPartner._id === id || state.selectedPartner.id === id)) {
          Object.assign(state.selectedPartner, payload)
        }
      })
      .addCase(updatePartnerProfile.rejected, (state, action) => {
        state.loading.status = false
        state.error = action.payload
      })

    // verifyDocument
    builder
      .addCase(verifyDocument.pending, state => {
        state.loading.document = true
        state.error = null
      })
      .addCase(verifyDocument.fulfilled, (state, action) => {
        state.loading.document = false
        const { documentId, status } = action.payload
        if (state.selectedPartner?.documents) {
          const doc = state.selectedPartner.documents.find(
            d => d._id === documentId || d.id === documentId
          )
          if (doc) doc.status = status
        }
      })
      .addCase(verifyDocument.rejected, (state, action) => {
        state.loading.document = false
        state.error = action.payload
      })

    // fetchActiveOrders
    builder
      .addCase(fetchActiveOrders.pending, state => {
        state.loading.list = true
        state.error = null
      })
      .addCase(fetchActiveOrders.fulfilled, (state, action) => {
        state.loading.list = false
        state.activeOrders = action.payload ?? []
      })
      .addCase(fetchActiveOrders.rejected, (state, action) => {
        state.loading.list = false
        state.error = action.payload
      })

    // fetchPartnerEarnings
    builder
      .addCase(fetchPartnerEarnings.pending, state => {
        state.loading.earnings = true
        state.error = null
      })
      .addCase(fetchPartnerEarnings.fulfilled, (state, action) => {
        state.loading.earnings = false
        state.earnings = action.payload
      })
      .addCase(fetchPartnerEarnings.rejected, (state, action) => {
        state.loading.earnings = false
        state.error = action.payload
      })

    // settleCOD
    builder
      .addCase(settleCOD.pending, state => {
        state.loading.cod = true
        state.error = null
      })
      .addCase(settleCOD.fulfilled, state => {
        state.loading.cod = false
      })
      .addCase(settleCOD.rejected, (state, action) => {
        state.loading.cod = false
        state.error = action.payload
      })

    // getPartnerRatingsThunk
    builder
      .addCase(getPartnerRatingsThunk.pending, state => {
        state.loading.list = true
        state.error = null
      })
      .addCase(getPartnerRatingsThunk.fulfilled, (state, action) => {
        state.loading.list = false
        const payload = action.payload || {}
        state.ratings = payload.ratings ?? []
      })
      .addCase(getPartnerRatingsThunk.rejected, (state, action) => {
        state.loading.list = false
        state.error = action.payload
      })

    // fetchPartnerOrders
    builder
      .addCase(fetchPartnerOrders.pending, state => {
        state.loading.orders = true
        state.error = null
      })
      .addCase(fetchPartnerOrders.fulfilled, (state, action) => {
        state.loading.orders = false
        const payload = action.payload || {}
        state.partnerOrders = payload.data ?? []
        state.partnerOrdersPagination = {
          currentPage: payload.currentPage ?? 1,
          totalPages: payload.totalPages ?? 1,
          totalCount: payload.totalCount ?? state.partnerOrders.length,
          itemsPerPage: payload.limit ?? 20,
        }
      })
      .addCase(fetchPartnerOrders.rejected, (state, action) => {
        state.loading.orders = false
        state.error = action.payload
      })
  },
})

export const {
  setFilters,
  resetFilters,
  clearError,
  clearSelectedPartner,
  setSelectedPartner,
} =
  deliveryPartnerSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectPartners = state => state.DeliveryPartner?.partners ?? []
export const selectSelectedPartner = state => state.DeliveryPartner?.selectedPartner
export const selectActiveOrders = state => state.DeliveryPartner?.activeOrders ?? []
export const selectFiltersCriteria = state => state.DeliveryPartner?.filtersCriteria ?? {}
export const selectEarnings = state => state.DeliveryPartner?.earnings
export const selectPartnerLoading = state =>
  state.DeliveryPartner?.loading ?? initialState.loading
export const selectPartnerError = state => state.DeliveryPartner?.error
export const selectPagination = state =>
  state.DeliveryPartner?.pagination ?? initialState.pagination
export const selectRatings = state => state.DeliveryPartner?.ratings ?? []
export const selectPartnerOrders = state => state.DeliveryPartner?.partnerOrders ?? []
export const selectPartnerOrdersPagination = state =>
  state.DeliveryPartner?.partnerOrdersPagination ?? initialState.partnerOrdersPagination

export default deliveryPartnerSlice.reducer
